import React from "react";
import {
  Button,
  InputField,
  Switch,
  Tag,
  Label,
  Divider,
  cn,
} from "@jem-open/jem-ui";
import { RosterConfig, SiteDefaults } from "../services/model";
import { InheritedField } from "./InheritedField";
import { GeofenceMapEditor } from "./GeofenceMapEditor";

export interface RosterConfigSectionProps {
  roster: RosterConfig;
  siteDefaults: SiteDefaults;
  onChange: (rosterId: string, patch: Partial<RosterConfig>) => void;
  onRevertField: (rosterId: string, field: "lateArrival") => void;
  onToggleGeofence: (rosterId: string, useSite: boolean) => void;
  onSave: (rosterId: string) => void;
  onDiscard: (rosterId: string) => void;
  className?: string;
}

/**
 * One Roster's full configuration row, rendered inside the Accordion.
 *
 * Sections:
 *   1. Required headcount + assigned/clocked-in counts (Roster-only)
 *   2. Geofence: Switch + (when on) override fields + map preview
 *   3. Late arrival threshold (inheritable)
 *   4. Per-Roster Save / Discard bar (only visible when dirty)
 */
export const RosterConfigSection: React.FC<RosterConfigSectionProps> = ({
  roster,
  siteDefaults,
  onChange,
  onRevertField,
  onToggleGeofence,
  onSave,
  onDiscard,
  className,
}) => {
  const effectiveLat = roster.useSiteGeofence
    ? siteDefaults.latitude
    : roster.geofenceLatitude ?? siteDefaults.latitude;
  const effectiveLng = roster.useSiteGeofence
    ? siteDefaults.longitude
    : roster.geofenceLongitude ?? siteDefaults.longitude;
  const effectiveRadius = roster.useSiteGeofence
    ? siteDefaults.geofenceRadius
    : roster.geofenceRadius ?? siteDefaults.geofenceRadius;

  const hasMultipleShifts =
    roster.shiftTypes !== undefined && roster.shiftTypes.length > 1;

  return (
    <div className={cn("space-y-md py-sm", className)}>
      {/* ---------- Headcount block ---------- */}
      <div>
        <Label>
          <span className="text-sm font-semibold text-greyscale-text-title">
            Required headcount
          </span>
        </Label>

        {hasMultipleShifts ? (
          <div className="mt-sm space-y-sm">
            <p className="text-xs text-greyscale-text-caption">
              This Roster runs more than one shift type. Set the target
              headcount per shift, or leave blank to fall back to assigned.
            </p>
            <div className="space-y-xs">
              {(roster.shiftTypes ?? []).map((shift) => (
                <div
                  key={shift.id}
                  className="flex flex-wrap items-center gap-md"
                >
                  <div className="min-w-[180px]">
                    <p className="text-sm font-semibold text-greyscale-text-title">
                      {shift.type}
                    </p>
                    <p className="text-xs text-greyscale-text-caption">
                      {shift.window} · {shift.assignedHeadcount} assigned
                    </p>
                  </div>
                  <InputField
                    type="number"
                    min={0}
                    value={shift.requiredHeadcount}
                    placeholder="e.g. 40"
                    className="w-[160px]"
                    onChange={(e) => {
                      const shiftTypes = roster.shiftTypes ?? [];
                      const newShifts = shiftTypes.map((s) =>
                        s.id === shift.id
                          ? { ...s, requiredHeadcount: e.target.value }
                          : s
                      );
                      onChange(roster.id, {
                        shiftTypes: newShifts,
                        dirty: true,
                      });
                    }}
                  />
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="mt-xxs sm:max-w-[50%]">
            <InputField
              type="number"
              min={0}
              value={roster.requiredHeadcount}
              onChange={(e) =>
                onChange(roster.id, {
                  requiredHeadcount: e.target.value,
                  dirty: true,
                })
              }
              placeholder="e.g. 40"
            />
          </div>
        )}
      </div>

      <Divider />

      {/* ---------- Geofence: Switch + override fields + map preview ---------- */}
      <div className="space-y-sm">
        <div className="flex items-center justify-between gap-md">
          <div>
            <Label className="flex items-center gap-xs">
              <span className="text-sm font-semibold text-greyscale-text-title">
                Geofence
              </span>
              {!roster.useSiteGeofence && (
                <Tag variant="processing" size="sm">
                  Custom
                </Tag>
              )}
            </Label>
            <p className="mt-xxs text-xs text-greyscale-text-caption">
              {roster.useSiteGeofence
                ? `Same as ${siteDefaults.siteName}. Clock events validate against the Site's geofence.`
                : `This Roster has its own geofence. Changes to ${siteDefaults.siteName} won't affect it.`}
            </p>
          </div>
          <div className="flex items-center gap-xs">
            <span className="text-sm text-greyscale-text-body">
              Use custom geofence
            </span>
            <Switch
              checked={!roster.useSiteGeofence}
              onCheckedChange={(checked) =>
                onToggleGeofence(roster.id, !checked)
              }
              aria-label="Use custom geofence"
            />
          </div>
        </div>

        {!roster.useSiteGeofence && (
          <GeofenceMapEditor
            latitude={effectiveLat}
            longitude={effectiveLng}
            radiusMeters={effectiveRadius}
            source="override"
            siteName={siteDefaults.siteName}
            onCoordsChange={(lat, lng) =>
              onChange(roster.id, {
                geofenceLatitude: lat,
                geofenceLongitude: lng,
                dirty: true,
              })
            }
          >
            <div className="grid gap-md sm:grid-cols-2">
              <InputField
                label="Latitude"
                value={effectiveLat}
                onChange={(e) =>
                  onChange(roster.id, {
                    geofenceLatitude: e.target.value,
                    dirty: true,
                  })
                }
              />
              <InputField
                label="Longitude"
                value={effectiveLng}
                onChange={(e) =>
                  onChange(roster.id, {
                    geofenceLongitude: e.target.value,
                    dirty: true,
                  })
                }
              />
            </div>
            <InputField
              label="Radius (m)"
              type="number"
              value={effectiveRadius}
              onChange={(e) =>
                onChange(roster.id, {
                  geofenceRadius: e.target.value,
                  dirty: true,
                })
              }
            />
          </GeofenceMapEditor>
        )}
      </div>

      <Divider />

      {/* ---------- Late arrival threshold (inheritable) ---------- */}
      <InheritedField
        label="Late arrival threshold (minutes)"
        source={roster.lateArrivalSource}
        siteName={siteDefaults.siteName}
        siteDefaultLabel={`${siteDefaults.lateArrivalThreshold} minutes`}
        onRevert={() => onRevertField(roster.id, "lateArrival")}
      >
        <div className="flex items-center gap-xs sm:w-1/2">
          {roster.lateArrivalSource === "site" ? (
            <>
              <InputField
                type="number"
                value={siteDefaults.lateArrivalThreshold}
                disabled
                className="flex-1"
              />
              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  onChange(roster.id, {
                    lateArrivalSource: "override",
                    lateArrivalOverrideValue: siteDefaults.lateArrivalThreshold,
                    dirty: true,
                  })
                }
              >
                Override
              </Button>
            </>
          ) : (
            <InputField
              type="number"
              value={roster.lateArrivalOverrideValue ?? ""}
              onChange={(e) =>
                onChange(roster.id, {
                  lateArrivalOverrideValue: e.target.value,
                  dirty: true,
                })
              }
              className="flex-1"
            />
          )}
        </div>
      </InheritedField>

      <Divider />

      {/* ---------- Per-Roster Save / Discard ---------- */}
      <div className="flex items-center justify-end gap-xs">
        {roster.dirty && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => onDiscard(roster.id)}
          >
            Discard
          </Button>
        )}
        <Button
          variant="default"
          size="sm"
          disabled={!roster.dirty}
          onClick={() => onSave(roster.id)}
        >
          Save changes
        </Button>
      </div>
    </div>
  );
};

export default RosterConfigSection;
